import { HelpCircle } from 'lucide-react';
import { dismissRequest, isRequestDismissed, useCaptureRequest } from '../hooks/useCaptureRequest';
import { Button } from '@/components/ui/button';

interface Props {
  deviceId: string;
  /** True when the device actually has a browsable web UI to send them to. */
  webUiAvailable: boolean;
  /** Take the user to the Web UI tab, where the recording actually happens. */
  onGoToWebUi: () => void;
  /** Suppressed on the Web UI tab itself, which makes the offer inline. */
  hidden?: boolean;
}

/**
 * The standing ask for this device, shown across the device page rather than
 * only on the Web UI tab.
 *
 * Someone looking at a switch is rarely sitting on the Web UI tab, so an offer
 * that only appears there is an offer almost nobody sees. This is the call to
 * action; the Web UI tab holds the actual consent and controls, because that
 * is where the recording has to happen.
 *
 * It renders nothing at all when there is nothing outstanding, which is the
 * normal case — the server only returns an ask that is unretired and not yet
 * answered often enough, so the only judgement left here is whether this
 * person waved it away.
 */
export default function DeviceCaptureRequestBanner({
  deviceId,
  webUiAvailable,
  onGoToWebUi,
  hidden = false,
}: Props) {
  const { data } = useCaptureRequest(deviceId);
  const request = data?.request ?? null;

  // No web UI means no way to answer, so asking would be an interruption with
  // nothing behind it.
  if (hidden || !webUiAvailable || !request || isRequestDismissed(request.id)) {
    return null;
  }

  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3 rounded-md border border-brand/30 bg-brand-soft/40 p-3">
      <div className="flex items-start gap-2">
        <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" />
        <div>
          <p className="text-sm font-medium">Help us improve this device</p>
          <p className="text-sm text-muted-foreground">
            We support this device, but we have not yet worked out {request.ask}. Open the
            Web UI tab and walk us through it — you review the recording before anything is
            shared.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onGoToWebUi}>
          Show us how
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // Dismissing here dismisses it everywhere: it is the same ask, and
            // re-offering it on the next tab would make "Not now" meaningless.
            dismissRequest(request.id);
          }}
        >
          Not now
        </Button>
      </div>
    </div>
  );
}
